import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { sessionStorageManager } from '@/lib/utils'
import fullRoutes, {IMenu} from './routes';
import ajax from '@/lib/fetch'

// routeConfig
const router = createRouter({
  history: createWebHistory(''),
  routes: [
    {
      path: "",
      name: "home",
      redirect: '/account',
    },
    {
      path: "/login",
      name: "login",
      component: () => import("../pages/login/index.vue"),
    },
  ] as readonly RouteRecordRaw[]
});

// 根据后端返回的权限ID过滤路由
function filterRoutesByPermission(permissionMenus: MenuItemRes[]): RouteRecordRaw[] {
  // const permissionIds = (menuJson || []).map((item: MenuItemRes) => item.id)
  const flatRoutes = flattenRoutes(fullRoutes)
  return flatRoutes.filter(route => {
    const routeName = route.name as string
    const hasPermissionMenu = (permissionMenus || []).find(i => i.id === routeName)
    // return permissionIds.includes(routeName)
    if (hasPermissionMenu) {
      route.meta = hasPermissionMenu.tabs
      return true
    }
    return false
  })
}

// 将嵌套路由扁平化
function flattenRoutes(routes: any[]): RouteRecordRaw[] {
  const result: RouteRecordRaw[] = []
  
  routes.forEach(route => {
    if (route.children) {
      result.push(...flattenRoutes(route.children))
    } else if (route.component) {
      result.push(route as RouteRecordRaw)
    }
  })
  
  return result
}

interface MenuItemRes {
  id: string,
  label: string,
  tabs: string[]
}

// 初始化动态路由
export async function getPermissionRoutes() {
  try {
    // 获取后端返回的权限列表
    const response = await ajax.get('/api/plan/info')
    const { planConfig: { menuJson} } = response
    
    return menuJson || []
  } catch (error) {
    console.error('初始化动态路由失败:', error)
    return []
  }
}

// 初始化动态路由
export async function setupDynamicRoutes() {
  try {
    const menuJson = await getPermissionRoutes()
    // const menuJson = [{"id": "account", "tabs": ["all"], "label": "账号资产"}, {"id": "material-list", "tabs": ["tiktok"], "label": "素材资产"}, {"id": "target", "tabs": ["focus"], "label": "截流线索"}, {"id": "up-owner", "tabs": ["tiktok"], "label": "博主资产"}, {"id": "missions", "tabs": ["keep", "alive", "issue", "edit_user_info", "direct", "story", "flow"], "label": "运营任务"}, {"id": "material-mission", "tabs": ["single", "homepage", "news", "up"], "label": "采集任务"}, {"id": "pull", "tabs": ["focus"], "label": "截流任务"}, {"id": "post-plan", "tabs": ["tiktok"], "label": "视频集合"}, {"id": "report", "tabs": ["account", "feels", "story"], "label": "基础报表"}, {"id": "logs", "tabs": ["execute", "feels", "story", "keep", "update", "direct", "alive", "filter", "pull"], "label": "系统报表"}, {"id": "chat", "tabs": ["all", "read", "unread"], "label": "SCRUM"}, {"id": "flow", "tabs": ["base"], "label": "基础流水线"}, {"id": "self-report", "name": "定制报表", "tabs": ["account", "feels"]}]
    
    // 根据权限过滤路由
    const dynamicRoutes = filterRoutesByPermission(menuJson)
    // 添加动态路由
    dynamicRoutes.forEach(route => {
      if (!router.hasRoute(route.name!)) {
        router.addRoute(route)
      }
    })
    
    // 确保有一个默认路由
    if (!router.hasRoute('home')) {
      router.addRoute({
        path: '',
        name: 'home',
        redirect: dynamicRoutes[0]?.path || '/login'
      })
    }
    return advancedFilterRoutes(fullRoutes, menuJson)
  } catch (error) {
    console.error('初始化动态路由失败:', error)
    return []
  }
}

export interface MenuPermission {
  id: string
  label: string
  tabs: string[]
}

/**
 * 高级菜单过滤函数（支持更多配置）
 */
export function advancedFilterRoutes(
  fullRoutes: IMenu[], 
  permissions: MenuPermission[],
  options: {
    includeHidden?: boolean // 是否包含hidden=true的菜单
    preserveStructure?: boolean // 是否保持完整的菜单结构
  } = {}
): IMenu[] {
  const { includeHidden = false, preserveStructure = false } = options
  const permissionIds = permissions.map(p => p.id)
  
  const filterRecursive = (menus: IMenu[]): IMenu[] => {
    const result: IMenu[] = []
    
    for (const menu of menus) {
      // 跳过隐藏的菜单（除非明确指定包含）
      if (menu.hidden && !includeHidden) {
        continue
      }
      
      const clonedMenu = { ...menu }
      let shouldInclude = false
      
      if (clonedMenu.children && clonedMenu.children.length > 0) {
        // 处理有子菜单的菜单项
        const filteredChildren = filterRecursive(clonedMenu.children)
        
        if (filteredChildren.length > 0) {
          // 有权限的子菜单，保留父菜单
          result.push({ ...clonedMenu, children: filteredChildren })
          shouldInclude = true
        } else if (permissionIds.includes(clonedMenu.name as string)) {
          // 父菜单本身有权限但没有子菜单权限
          const { children, ...singleMenu } = clonedMenu
          result.push(singleMenu as IMenu)
          shouldInclude = true
        }
      } else {
        // 处理叶子节点
        if (permissionIds.includes(clonedMenu.name as string)) {
          const permission = permissions.find(p => p.id === clonedMenu.name)
          result.push({
            ...clonedMenu,
            meta: {
              ...clonedMenu.meta,
              tabs: permission?.tabs || []
            }
          })
          shouldInclude = true
        }
      }
      
      // 如果配置了保持结构，即使没有权限也保留空结构（用于调试）
      if (preserveStructure && !shouldInclude && clonedMenu.children) {
        result.push({ ...clonedMenu, children: [] })
      }
    }
    
    return result
  }
  
  return filterRecursive(fullRoutes)
}

router.beforeEach((_to, _from, next) => {
  if (_to.path === '/login') {
    next();
    return;
  }
  // 检查 token 是否存在
  const token = sessionStorageManager.getToken();
  if (!token) {
    // 定向跳转到登录页面
    next({
      path: '/login',
      query: { redirect: _to.fullPath } // 保存目标路由路径
    });
    return;
  }
  // 已认证，否则正常跳转
  next();
});
export default router
